import { TranslateService } from '@ngx-translate/core';
import {
  getOfferDetailsFormLabel,
  getOperatingConditionCategoryLabel,
} from '@seu-helpers';
import {
  SeuCalculationUnitItem,
  SeuChangelogChange,
  SeuChangelogChangeFlag,
  SeuChangelogItem,
  SeuEditSession,
  SeuFromApiConverter,
  SeuOperatingConditionCategory,
} from '@seu-types';
import { isEmpty, isNil } from 'lodash-es';
import {
  SeuApiCalculationCombinationDto,
  SeuApiChangeLogEntry,
  SeuApiEditSessionDto,
} from '../api';

export const editSessionConverter: SeuFromApiConverter<
  SeuApiEditSessionDto,
  SeuEditSession
> = {
  fromApi: (input) => {
    return {
      ...input,
      id: String(input.id),
    };
  },
};

export const changelogItemConverter = (
  translateService: TranslateService
): SeuFromApiConverter<SeuApiChangeLogEntry, SeuChangelogItem> => ({
  fromApi: (input) => {
    return {
      ...input,
      id: String(input.id),
      editSessionId: String(input.editSessionId),
      changes: input.changes
        .map((change) => {
          if (
            Array.isArray(change.oldValue) ||
            Array.isArray(change.newValue)
          ) {
            // If values are arrays, the change is for calculations
            if (input.type !== 'CALCULATION_COMBINATION_CHANGED') {
              return null;
            }

            const oldValue =
              (change.oldValue as SeuApiCalculationCombinationDto[] | null) ??
              [];
            const newValue =
              (change.newValue as SeuApiCalculationCombinationDto[] | null) ??
              [];

            const flag = !oldValue.length
              ? 'new'
              : !newValue.length
                ? 'deleted'
                : 'changed';

            const converted = {
              fieldName: change.fieldName,
              oldValue: oldValue.map((item) => ({
                globalParameterSetId: String(item.globalParameterSetId),
                interconnectionId: String(item.interconnectionId),
                operatingConditionIds: item.operatingConditionIds?.map(String),
              })),
              newValue: newValue.map((item) => ({
                globalParameterSetId: String(item.globalParameterSetId),
                interconnectionId: String(item.interconnectionId),
                operatingConditionIds: item.operatingConditionIds?.map(String),
              })),
              flag,
            } as SeuChangelogChange;

            return converted;
          }

          let configKey = input.configKey;

          if (input.type === 'OPERATING_CONDITION_CHANGED') {
            configKey = 'inputStream';
          }

          if (input.type === 'EMISSION_LIMITS_CHANGED') {
            configKey = 'emissionLimit';
          }

          // Determine the flag based on the presence of oldValue and newValue
          const flag: SeuChangelogChangeFlag = isNil(change.oldValue)
            ? 'new'
            : isNil(change.newValue)
              ? 'deleted'
              : 'changed';

          if (
            typeof change.oldValue === 'string' ||
            typeof change.newValue === 'string' ||
            typeof change.oldValue === 'number' ||
            typeof change.newValue === 'number'
          ) {
            if (
              (isNil(change.newValue) && change.oldValue === '') ||
              (isNil(change.oldValue) && change.newValue === '')
            ) {
              return null;
            }

            let oldValueAsString =
              typeof change.oldValue === 'number'
                ? String(change.oldValue)
                : (change.oldValue as string | null | undefined);
            let newValueAsString =
              typeof change.newValue === 'number'
                ? String(change.newValue)
                : (change.newValue as string | null | undefined);

            let fieldName = change.fieldName;

            if (configKey) {
              fieldName = `${configKey}.form.${change.fieldName}`;
            }
            if (input.type === 'DETAILS_CHANGED') {
              fieldName = getOfferDetailsFormLabel(change.fieldName);
            }

            if (input.type === 'OPERATING_CONDITION_CHANGED') {
              if (change.fieldName === 'title') {
                fieldName = 'operating-condition.form.title';
              } else if (change.fieldName === 'description') {
                fieldName = 'operating-condition.form.description';
              } else if (change.fieldName === 'categories') {
                fieldName = 'operating-condition.form.categories';
              }

              if (change.fieldName === 'categories') {
                oldValueAsString = change.oldValue
                  ? translateService.instant(
                      getOperatingConditionCategoryLabel(
                        change.oldValue as SeuOperatingConditionCategory
                      )
                    )
                  : null;
                newValueAsString = change.newValue
                  ? translateService.instant(
                      getOperatingConditionCategoryLabel(
                        change.newValue as SeuOperatingConditionCategory
                      )
                    )
                  : null;
              }
            }

            if (input.type === 'GLOBAL_PARAMETER_SET_CHANGED') {
              if (change.fieldName === 'title') {
                fieldName = 'global-parameter.form.title';
              } else if (change.fieldName === 'description') {
                fieldName = 'global-parameter.form.description';
              }
            }

            if (input.type === 'COMPONENT_CONFIGURATION_CHANGED') {
              if (change.fieldName === 'name') {
                fieldName = 'components.form.name';
              }
            }

            if (change.fieldName === 'presetId') {
              return null;
            }

            return {
              ...change,
              oldValue: oldValueAsString,
              newValue: newValueAsString,
              fieldName,
              flag,
            };
          }

          let fieldName = change.fieldName;

          if (fieldName === 'inputs') {
            // return null for all inputs that are also reflected on top level of the change
            if (
              change.oldValue?.name === 'name' ||
              change.newValue?.name === 'name' ||
              change.oldValue?.name === 'title' ||
              change.newValue?.name === 'title' ||
              change.oldValue?.name === 'description' ||
              change.newValue?.name === 'description' ||
              change.oldValue?.name === 'componentType' ||
              change.newValue?.name === 'componentType'
            ) {
              return null;
            }
          }

          if (configKey) {
            if (fieldName === 'inputs') {
              const key = change.newValue?.name ?? change.oldValue?.name ?? '';
              fieldName = `${configKey}.form.${key}`;
            } else {
              fieldName = `${configKey}.form.${change.fieldName}`;
            }
          }

          const oldValue: SeuCalculationUnitItem | null = change.oldValue
            ? {
                unit: change.oldValue.unitValue?.unit ?? '',
                value: change.oldValue.unitValue?.value,
              }
            : null;
          const newValue: SeuCalculationUnitItem | null = change.newValue
            ? {
                unit: change.newValue.unitValue?.unit ?? '',
                value: change.newValue.unitValue?.value,
              }
            : null;

          if (
            (Array.isArray(newValue?.value) && isEmpty(newValue?.value)) ||
            (Array.isArray(oldValue?.value) && isEmpty(oldValue?.value))
          ) {
            return null;
          }

          return {
            fieldName,
            oldValue,
            newValue,
            flag,
          };
        })
        .filter((change) => change !== null),
    };
  },
});
